/**
 * Real-user Core Web Vitals collection.
 *
 * Field data is the only honest answer to "did the performance work help?" —
 * Lighthouse runs a simulated Moto G, our parents are on real Indian networks.
 *
 * Design notes:
 *   - Metrics are read through `web-vitals` with buffered entries, so
 *     registering after FCP (we start from initAnalytics, post-FCP) still
 *     captures LCP / FCP / TTFB correctly.
 *   - Samples are sent to the existing `ingest-event` function as
 *     `event: "web_vital"`, so nothing new is needed on the write path and the
 *     samples join to visitors/sessions like every other funnel event.
 *   - Sampling is sticky per session (25%) to keep Nano write volume flat, but
 *     the logged-in course player is sampled at 100%: that is where paying
 *     subscribers spend their time and where a regression costs us most.
 *   - This is a SPA, so CLS and INP keep accumulating across client-side
 *     navigations. We attribute them to the route that was on screen when the
 *     value last changed and flush on route change / page hide, otherwise every
 *     interaction delay in the player would be blamed on the landing page.
 *   - Never mirrored to GA4 / Meta — this is internal measurement only.
 */
import { supabase } from "@/integrations/supabase/client";
import { getDeviceType, getRelease, getSessionId, getVisitorId } from "@/lib/telemetry/session";

const SAMPLE_RATE = 0.25;
const SAMPLE_KEY = "lx_vitals_sampled";

/** Routes that make up the logged-in learning experience. */
const PLAYER_ROUTE_PATTERNS = [
  /^\/course\//,
  /^\/course-trial\//,
  /^\/learning-journeys/,
  /^\/journey/,
];

export function isPlayerRoute(pathname: string): boolean {
  return PLAYER_ROUTE_PATTERNS.some((re) => re.test(pathname));
}

/** Coarse segment used for admin reporting. */
export function routeSegment(pathname: string): "player" | "site" {
  return isPlayerRoute(pathname) ? "player" : "site";
}

/**
 * Synchronous "is there a stored session?" check.
 *
 * Only looks for the presence of a Supabase auth-token key; the token value is
 * never read, parsed or sent.
 */
export function hasStoredSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && /^sb-.*-auth-token$/.test(key) && window.localStorage.getItem(key)) return true;
    }
  } catch {
    /* storage blocked — fall back to the async read */
  }
  return false;
}


let started = false;
let inMemorySampled: boolean | null = null;
/**
 * Whether this sample came from a signed-in visitor.
 *
 * Seeded synchronously from the stored session, because LCP/FCP/TTFB are sent
 * within the first moments of the load — before `auth.getSession()` resolves.
 * Reading it asynchronously stamped every sample `authed: false`, including
 * samples from course-player routes that only signed-in learners can reach,
 * which made the "signed-in only" admin view permanently empty.
 */
let authed = hasStoredSession();

let currentRoute = "/";
let loadRoute = "/";

/** Sticky per-session sampling decision for ordinary marketing/site traffic. */
export function isVitalsSampled(rate = SAMPLE_RATE): boolean {
  try {
    const stored = sessionStorage.getItem(SAMPLE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    const sampled = Math.random() < rate;
    sessionStorage.setItem(SAMPLE_KEY, sampled ? "1" : "0");
    return sampled;
  } catch {
    if (inMemorySampled === null) inMemorySampled = Math.random() < rate;
    return inMemorySampled;
  }
}

/**
 * Player sessions are always measured; everything else falls back to the
 * sticky 25% sample.
 */
export function shouldCollect(pathname: string): boolean {
  return isPlayerRoute(pathname) || isVitalsSampled();
}

export interface VitalSample {
  name: string;
  value: number;
  rating: string;
  navigationType?: string;
  // Shape differs per metric (CLSAttribution / INPAttribution / ...), so it is
  // kept opaque here and narrowed in attributionTarget().
  attribution?: unknown;

}

/**
 * Collapses a concrete URL into a route shape so aggregation is meaningful:
 * `/course/8f3c-.../unit/12` -> `/course/:id/unit/:id`.
 */
export function routeShape(pathname: string): string {
  const shaped = pathname
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ":id";
      if (/^\d+$/.test(seg)) return ":id";
      if (seg.length > 24) return ":id";
      return seg;
    })
    .join("/");
  return shaped.slice(0, 120) || "/";
}

/** First 80 chars of the most useful attribution field, for triage only. */
export function attributionTarget(sample: VitalSample): string | undefined {
  const a = sample.attribution as
    | { element?: string; interactionTarget?: string; largestShiftTarget?: string }
    | undefined;
  const raw = a?.element ?? a?.interactionTarget ?? a?.largestShiftTarget;
  return typeof raw === "string" && raw ? raw.slice(0, 80) : undefined;
}

/**
 * Per-phase breakdown, so "LCP is 3.5s" becomes "render delay is 1.9s of it".
 * All values are milliseconds and only the phases the metric actually has are
 * written, keeping the props payload small.
 */
export function attributionPhases(sample: VitalSample): Record<string, number> {
  const a = sample.attribution as Record<string, unknown> | undefined;
  if (!a) return {};
  const keys: Record<string, string[]> = {
    LCP: ["timeToFirstByte", "resourceLoadDelay", "resourceLoadDuration", "elementRenderDelay"],
    INP: ["inputDelay", "processingDuration", "presentationDelay"],
    TTFB: ["waitingDuration", "dnsDuration", "connectionDuration", "requestDuration"],
  };
  const phases: Record<string, number> = {};
  for (const key of keys[sample.name] ?? []) {
    const value = a[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      phases[toSnake(key)] = Math.round(value);
    }
  }
  return phases;
}

function toSnake(key: string): string {
  return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function send(sample: VitalSample, route: string): void {
  try {
    void supabase.functions
      .invoke("ingest-event", {
        body: {
          event: "web_vital",
          props: {
            metric: sample.name,
            // CLS is unitless and small; everything else is milliseconds.
            value: sample.name === "CLS" ? Number(sample.value.toFixed(4)) : Math.round(sample.value),
            rating: sample.rating,
            navigation_type: sample.navigationType,
            release: getRelease(),
            route,
            segment: routeSegment(route),
            authed,
            soft_nav: route !== loadRoute,
            target: attributionTarget(sample),
            ...attributionPhases(sample),
          },

          visitor_id: getVisitorId(),
          session_id: getSessionId(),
          path: route,
          device_type: getDeviceType(),
        },
      })
      .catch(() => {
        /* best effort — never surface telemetry failures */
      });
  } catch {
    /* noop */
  }
}

/**
 * Latest value of an accumulating metric (CLS / INP) per route, flushed when
 * the route changes or the page is hidden. Keyed `route|metric` so one sample
 * per metric per route visit is written at most.
 */
const pending = new Map<string, VitalSample>();

function stash(sample: VitalSample, route: string): void {
  pending.set(`${route}|${sample.name}`, sample);
}

function flush(routeFilter?: string): void {
  for (const [key, sample] of [...pending.entries()]) {
    const route = key.slice(0, key.lastIndexOf("|"));
    if (routeFilter && route !== routeFilter) continue;
    pending.delete(key);
    send(sample, route);
  }
}

/**
 * Called on every client-side navigation. Flushes the accumulating metrics for
 * the route the visitor is leaving and starts collection when a previously
 * unsampled session enters the course player.
 */
export function noteVitalsRoute(pathname: string): void {
  const shaped = routeShape(pathname);
  if (shaped === currentRoute) return;
  flush(currentRoute);
  currentRoute = shaped;
  if (!started && isPlayerRoute(shaped)) void initWebVitals();
}

/**
 * Registers the Core Web Vitals listeners. Safe to call more than once.
 * No-ops outside the browser and for unsampled non-player sessions.
 */
export async function initWebVitals(): Promise<void> {
  if (started) return;
  if (typeof window === "undefined") return;
  loadRoute = routeShape(window.location.pathname);
  currentRoute = loadRoute;
  if (!shouldCollect(window.location.pathname)) return;
  started = true;

  // Whether the sample came from a signed-in learner/parent. Read once, then
  // kept in sync — lets the admin view isolate subscriber experience.
  try {
    const { data } = await supabase.auth.getSession();
    authed = Boolean(data.session);
    supabase.auth.onAuthStateChange((_event, session) => {
      authed = Boolean(session);
    });
  } catch {
    /* anonymous visitor */
  }

  try {
    // The "attribution" build is what populates `metric.attribution`
    // (largestShiftTarget for CLS, element for LCP, interactionTarget for INP).
    // The plain build reports values with no culprit, which is exactly the data
    // we need to chase a field-only CLS that Lighthouse cannot reproduce.
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import("web-vitals/attribution");

    // Paint/network metrics happen once per hard load — send immediately.
    onLCP((m) => send(m as VitalSample, currentRoute));
    onFCP((m) => send(m as VitalSample, currentRoute));
    onTTFB((m) => send(m as VitalSample, currentRoute));
    // CLS/INP accumulate across the SPA session — attribute per route.
    onCLS((m) => stash(m as VitalSample, currentRoute), { reportAllChanges: true });
    onINP((m) => stash(m as VitalSample, currentRoute), { reportAllChanges: true });
  } catch {
    /* web-vitals unavailable — measurement is optional */
  }

  const flushAll = () => flush();
  window.addEventListener("pagehide", flushAll);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAll();
  });
}
